import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  LinearProgress,
  Pagination,
  Stack,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as VerifyIcon,
  Download as DownloadIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { apiService } from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import { useTableSort } from '../../hooks/useTableSort';
import { useRole } from '../../hooks/useRole';
import SortableTableHead from '../../components/Table/SortableTableHead';
import PageLayout from '../../components/Layout/PageLayout';
import BackButton from '../../components/BackButton';
import ExcelImportErrorDialog from '../../components/Dialogs/ExcelImportErrorDialog';
import EditValidationErrorDialog from '../../components/Dialogs/EditValidationErrorDialog';
import { SiteSlump, SiteSlumpQueryModel, Form17ExportRequest } from '../../types/siteSlump';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isManagerRole } from '../../utils/roleUtils';
import { useAuth } from '../../contexts/AuthContext';

const SiteSlumpPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, isAdminOrManager, isStaff, isReader, canVerify } = useRole();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Excel 相關狀態
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const [slumps, setSlumps] = useState<SiteSlump[]>([]);
  const [loading, setLoading] = useState(false);

  // Contract numbers for dropdown
  const [contractNumbers, setContractNumbers] = useState<string[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);

  // 表格排序功能
  const getValue = (item: SiteSlump, key: string) => {
    switch (key) {
      case 'id':
        return item.Report_ID;
      case 'rfid':
        return item.RFID;
      case 'uploadBy':
        return item.Upload_By;
      case 'docketNo':
        return item.Docket_No;
      case 'contractNo':
        return item.Contract_No;
      case 'mixID':
        return item.Mix_ID;
      case 'grade':
        return item.Grade;
      case 'plant':
        return item.Plant;
      case 'supplier':
        return item.Supplier;
      case 'designSlump':
        return item.Design_Slump;
      case 'testDate':
        return new Date(item.Date || '');
      case 'measuredSlump1':
        return item.Measured_Slump_1;
      case 'measuredSlump2':
        return item.Measured_Slump_2;
      case 'measuredSlumpAver':
        return item.Measured_Slump_Aver;
      case 'resultOfSlumpTest':
        return item.Result_Of_Slump_Test;
      case 'testedBy':
        return item.Slump_Carried_Out_By;
      case 'verifiedBy':
        return item.VerifyBy;
      case 'verificationDate':
        return new Date(item.VerifyDate || '');
      case 'isVerified':
        return item.Verify;
      case 'uploadDate':
        return new Date(item.Upload_date || '');
      case 'uploadBy':
        return item.Upload_By;
      default:
        return '';
    }
  };

  const { sortConfig, handleSort, sortedData } = useTableSort(slumps, getValue);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSlump, setEditingSlump] = useState<SiteSlump | null>(null);
  const [queryParams, setQueryParams] = useState<SiteSlumpQueryModel>({
    Page: 1,
    PageSize: 20,
    Docket_No: '',
    RFID: '',
    Uploaded_By: '',
    Contract_No: '',
    startTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7天前
    endTime: new Date().toISOString().split('T')[0],
  });
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  // 驗證確認對話框狀態
  const [verifyDialog, setVerifyDialog] = useState({ open: false, id: 0, verify: '' });
  // 批量核實確認對話框狀態
  const [bulkVerifyDialog, setBulkVerifyDialog] = useState({ open: false, action: '' });
  // Excel 導入錯誤對話框狀態
  const [errorDialog, setErrorDialog] = useState({
    open: false,
    errors: [] as Array<{ rowNumber: number; errorMessage: string }>,
    successCount: 0,
    errorCount: 0,
    message: ''
  });
  // 編輯驗證錯誤對話框狀態
  const [editValidationErrorDialog, setEditValidationErrorDialog] = useState({
    open: false,
    errors: [] as string[],
    recordId: undefined as number | undefined,
    recordInfo: ''
  });
  // 分頁狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 20;

  const [formData, setFormData] = useState({
    Mix_ID: '',
    Date: '',
    Acceptable_range: '',
    Grade: '',
    Plant: '',
    Supplier: '',
    Design_Slump: '',
    No_Of_Truck: '',
    Docket_No: '',
    Time_Water_Add: '',
    Time_On_Site: '',
    Time_Of_Sampling: '',
    Time_Of_Test_1_Start: '',
    Time_Of_Test_1_Comp: '',
    Time_Of_Test_2_Start: '',
    Time_Of_Test_2_Comp: '',
    Time_Of_Test_Fin: '',
    Measured_Slump_1: '',
    Measured_Slump_2: '',
    Form_Of_Slump_1: '',
    Form_Of_Slump_2: '',
    Slump_plate_No: '',
    Result_Of_Slump_Test: '',
    Slump_Carried_Out_By: '',
    Total_Vol: '',
    Amb_Temp: '',
    Conc_Temp: '',
    Weather: '',
    Photo_No: '',
    Time_Of_Cube_Com: '',
    Certificate_No: '',
    Cube_Mark: '',
    Age_day: '',
    Mould_No: '',
    Cube_Carried_Out_By: '',
    RFID: '',
    Remark: '',
    Term_Contract: '',
    Work_Order_No: '',
    Contract_No: '',
    Job_title: '',
    Address: '',
    Concreting_Location: '',
    Slump_Project: '',
    Location_Modified: ''
  });

  // 使用狀態追蹤是否已經初始化
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
  const hasInitialSearch = useRef(false);

  // Fetch contract numbers on component mount
  useEffect(() => {
    const fetchContractNumbers = async () => {
      try {
        setLoadingContracts(true);
        const numbers = await apiService.getContractNumbers();
        setContractNumbers(numbers);
      } catch (error) {
        console.error('Error fetching contract numbers:', error);
        // 即使失敗也繼續，允許用戶自由輸入
      } finally {
        setLoadingContracts(false);
      }
    };

    fetchContractNumbers();
  }, []);

  // 從 URL 參數初始化查詢條件（只在首次加載時執行）
  useEffect(() => {
    if (urlParamsProcessed) return;

    const urlParams: Partial<SiteSlumpQueryModel> = {};

    // 讀取所有可能的 URL 參數
    const docketNo = searchParams.get('Docket_No');
    const rfid = searchParams.get('RFID');
    const uploadedBy = searchParams.get('Uploaded_By');
    const contractNo = searchParams.get('Contract_No');
    const startTime = searchParams.get('startTime');
    const endTime = searchParams.get('endTime');

    if (docketNo) urlParams.Docket_No = docketNo;
    if (rfid) urlParams.RFID = rfid;
    if (uploadedBy) urlParams.Uploaded_By = uploadedBy;
    if (contractNo) urlParams.Contract_No = contractNo;
    if (startTime) urlParams.startTime = startTime;
    if (endTime) urlParams.endTime = endTime;

    // 如果有 URL 參數，更新查詢條件
    if (Object.keys(urlParams).length > 0) {
      setQueryParams(prev => ({
        ...prev,
        ...urlParams,
        startTime: urlParams.startTime || prev.startTime,
        endTime: urlParams.endTime || prev.endTime
      }));
      setCurrentPage(1);
    }

    setUrlParamsProcessed(true);
  }, [searchParams, urlParamsProcessed]);

  // 初始加載時執行搜索（在 URL 參數處理完成後，只執行一次）
  useEffect(() => {
    if (urlParamsProcessed && !hasInitialSearch.current) {
      hasInitialSearch.current = true;
      fetchSlumps(currentPage);
    }
  }, [urlParamsProcessed]);

  // 當分頁改變時執行搜索
  useEffect(() => {
    if (urlParamsProcessed && hasInitialSearch.current) {
      fetchSlumps(currentPage);
    }
  }, [currentPage]);

  // 處理 Enter 鍵觸發搜索
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !loading) {
      event.preventDefault();
      // 驗證時間範圍
      if (queryParams.startTime && queryParams.endTime) {
        const startDate = new Date(queryParams.startTime);
        const endDate = new Date(queryParams.endTime);

        if (endDate < startDate) {
          showSnackbar('結束時間不能早於開始時間，請重新選擇', 'warning');
          return;
        }
      }
      setCurrentPage(1);
      fetchSlumps(1);
    }
  };

  const fetchSlumps = async (page: number = currentPage) => {
    try {
      setLoading(true);
      const response = await apiService.getSlump({
        ...queryParams,
        Page: page,
        PageSize: pageSize,
      });

      // 處理後端返回的響應
      if (response) {
        // 如果後端返回的是 SiteSlumpResponse 格式
        const siteSlumpResponse = response;
        setSlumps(siteSlumpResponse.data || []);
        setTotalCount(siteSlumpResponse.totalCount || 0);
        setTotalPages(Math.ceil((siteSlumpResponse.totalCount || 0) / pageSize));
      } else {
        // 默認情況
        setSlumps([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      showSnackbar(t('pages.siteSlump.messages.loadingError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 處理分頁變化
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    fetchSlumps(page);
  };

  // 批量核實所有記錄
  const handleBulkVerify = () => {
    setBulkVerifyDialog({ open: true, action: 'verify' });
  };

  // 批量還原核實所有記錄
  const handleBulkUnverify = () => {
    setBulkVerifyDialog({ open: true, action: 'unverify' });
  };

  // 確認批量核實操作
  const confirmBulkVerify = async () => {
    try {
      setLoading(true);
      const action = bulkVerifyDialog.action;
      const verifyValue = action === 'verify' ? 'T' : 'F';

      // 顯示處理中的提示
      showSnackbar(
        t('pages.siteSlump.messages.processingBulkOperation').replace('{count}', totalCount.toString()),
        'success'
      );

      queryParams.startTime = queryParams.startTime ? queryParams.startTime : "1970-01-01";
      queryParams.endTime = queryParams.endTime ? queryParams.endTime : "1970-01-01";

      // 使用後端批量API處理所有搜索結果
      const response = await apiService.bulkVerifySiteSlumps({
        queryParams: queryParams,
        verifyValue: verifyValue
      });


      if (response.data && response.data.success) {
        showSnackbar(
          t('pages.siteSlump.messages.bulkVerifySuccess').replace('{count}', response.data.processedCount).replace('{action}', action === 'verify' ? t('common.verifiedAction') : t('common.unverifiedAction')),
          'success'
        );

        // 刷新數據
        fetchSlumps(currentPage);
      } else {
        showSnackbar(
          t('pages.siteSlump.messages.bulkVerifyError') + ': ' + (response?.message || '未知錯誤'),
          'error'
        );
      }

    } catch (error: any) {
      console.error('批量核實失敗:', error);
      const errorMessage = error.response?.data?.message || error.message || t('pages.siteSlump.messages.bulkVerifyError');
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
      setBulkVerifyDialog({ open: false, action: '' });
    }
  };

  // 重置篩選條件
  const handleResetFilters = () => {
    const defaultParams: SiteSlumpQueryModel = {
      Page: 1,
      PageSize: 20,
      Docket_No: '',
      RFID: '',
      Uploaded_By: '',
      Contract_No: '',
      startTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endTime: new Date().toISOString().split('T')[0],
    };
    setQueryParams(defaultParams);
    setCurrentPage(1);
    // 重置後自動執行查詢
    fetchSlumps(1);
  };

  const handleSubmit = async () => {
    try {
      // 轉換數值字段為正確的類型
      const submitData = {
        ...formData,
        Measured_Slump_1: formData.Measured_Slump_1 ? parseFloat(formData.Measured_Slump_1) : null,
        Measured_Slump_2: formData.Measured_Slump_2 ? parseFloat(formData.Measured_Slump_2) : null,
        Age_day: formData.Age_day ? parseInt(formData.Age_day) : null
      };

      if (editingSlump) {
        await apiService.put(`/Site_Slump/${editingSlump.Report_ID}`, submitData);
        showSnackbar(t('pages.siteSlump.messages.dataSaved'), 'success');
      } else {
        await apiService.post('/Site_Slump', submitData);
        showSnackbar(t('pages.siteSlump.messages.dataSaved'), 'success');
      }
      setOpenDialog(false);
      resetForm();
      fetchSlumps();
    } catch (error: any) {
      // 檢查是否為驗證錯誤
      if (error.response?.status === 400 && error.response?.data?.validationErrors) {
        const validationErrors = error.response.data.validationErrors;
        const recordInfo = editingSlump
          ? `合約號: ${editingSlump.Contract_No}, MixID: ${editingSlump.Mix_ID}, Docket No: ${editingSlump.Docket_No}`
          : '';

        setEditValidationErrorDialog({
          open: true,
          errors: validationErrors,
          recordId: editingSlump?.Report_ID,
          recordInfo: recordInfo
        });
      } else {
        showSnackbar(t('pages.siteSlump.messages.saveError'), 'error');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('pages.siteSlump.confirmDelete'))) {
      try {
        await apiService.delete(`/Site_Slump/${id}`);
        showSnackbar(t('pages.siteSlump.messages.dataDeleted'), 'success');
        fetchSlumps();
      } catch (error) {
        showSnackbar((error as any).response?.data?.message, 'error');
      }
    }
  };

  const handleVerify = async (id: number, verify: string) => {
    setVerifyDialog({ open: true, id, verify });
  };

  const confirmVerify = async () => {
    try {
      await apiService.put(`/Site_Slump/${verifyDialog.id}/verify`, { IsVerified: verifyDialog.verify === 'F' ? true : false });
      showSnackbar(t('pages.siteSlump.messages.dataVerified'), 'success');
      fetchSlumps();
      setVerifyDialog({ open: false, id: 0, verify: '' });
    } catch (error) {
      showSnackbar(t('pages.siteSlump.messages.verifyError'), 'error');
    }
  };

  // 檢查記錄是否可以編輯
  const canEditRecord = (slump: SiteSlump) => {
    // 如果記錄已驗證，不能編輯
    if (slump.Verify === 'T') {
      return false;
    }

    // 檢查時間窗口（需要從後端獲取 daysToAdd 配置）
    const now = new Date();
    const daysToAdd = 7; // 默認7天，實際應該從系統配置獲取

    // 檢查上傳時間
    if (slump.Upload_date) {
      const uploadDate = new Date(slump.Upload_date);
      const uploadDeadline = new Date(uploadDate.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
      if (now > uploadDeadline) {
        return false;
      }
    }

    // 檢查創建時間
    if (slump.Created_date) {
      const createdDate = new Date(slump.Created_date);
      const createdDeadline = new Date(createdDate.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
      if (now > createdDeadline) {
        return false;
      }
    }

    return true;
  };

  // 獲取編輯限制信息
  const getEditRestrictionInfo = (slump: SiteSlump) => {
    if (slump.Verify === 'T') {
      return '此記錄已驗證，無法修改';
    }

    const now = new Date();
    const daysToAdd = 7; // 默認7天，實際應該從系統配置獲取

    if (slump.Upload_date) {
      const uploadDate = new Date(slump.Upload_date);
      const uploadDeadline = new Date(uploadDate.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
      if (now > uploadDeadline) {
        return `記錄上傳時間超過 ${daysToAdd} 天，無法修改 (上傳時間: ${uploadDate.toLocaleString()}, 截止時間: ${uploadDeadline.toLocaleString()})`;
      }
    }

    if (slump.Created_date) {
      const createdDate = new Date(slump.Created_date);
      const createdDeadline = new Date(createdDate.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
      if (now > createdDeadline) {
        return `記錄創建時間超過 ${daysToAdd} 天，無法修改 (創建時間: ${createdDate.toLocaleString()}, 截止時間: ${createdDeadline.toLocaleString()})`;
      }
    }

    return null;
  };

  const handleEdit = (slump: SiteSlump) => {
    // 檢查編輯權限
    if (!canEditRecord(slump)) {
      const restrictionInfo = getEditRestrictionInfo(slump);
      showSnackbar(restrictionInfo || '此記錄無法編輯', 'error');
      return;
    }

    setEditingSlump(slump);
    setFormData({
      Mix_ID: slump.Mix_ID || '',
      Acceptable_range: slump.Acceptable_range || '',
      Date: slump.Date?.split('T')[0] || '',
      Grade: slump.Grade || '',
      Plant: slump.Plant || '',
      Supplier: slump.Supplier || '',
      Design_Slump: slump.Design_Slump || '',
      No_Of_Truck: slump.No_Of_Truck || '',
      Docket_No: slump.Docket_No || '',
      Time_Water_Add: slump.Time_Water_Add || '',
      Time_On_Site: slump.Time_On_Site || '',
      Time_Of_Sampling: slump.Time_Of_Sampling || '',
      Time_Of_Test_1_Start: slump.Time_Of_Test_1_Start || '',
      Time_Of_Test_1_Comp: slump.Time_Of_Test_1_Comp || '',
      Time_Of_Test_2_Start: slump.Time_Of_Test_2_Start || '',
      Time_Of_Test_2_Comp: slump.Time_Of_Test_2_Comp || '',
      Time_Of_Test_Fin: slump.Time_Of_Test_Fin || '',
      Measured_Slump_1: slump.Measured_Slump_1?.toString() || '',
      Measured_Slump_2: slump.Measured_Slump_2?.toString() || '',
      Form_Of_Slump_1: slump.Form_Of_Slump_1 || '',
      Form_Of_Slump_2: slump.Form_Of_Slump_2 || '',
      Slump_plate_No: slump.Slump_plate_No || '',
      Result_Of_Slump_Test: slump.Result_Of_Slump_Test || '',
      Slump_Carried_Out_By: slump.Slump_Carried_Out_By || '',
      Total_Vol: slump.Total_Vol || '',
      Amb_Temp: slump.Amb_Temp || '',
      Conc_Temp: slump.Conc_Temp || '',
      Weather: slump.Weather || '',
      Photo_No: slump.Photo_No || '',
      Time_Of_Cube_Com: slump.Time_Of_Cube_Com || '',
      Certificate_No: slump.Certificate_No || '',
      Cube_Mark: slump.Cube_Mark || '',
      Age_day: slump.Age_day?.toString() || '',
      Mould_No: slump.Mould_No || '',
      Cube_Carried_Out_By: slump.Cube_Carried_Out_By || '',
      RFID: slump.RFID || '',
      Remark: slump.Remark || '',
      Term_Contract: slump.Term_Contract || '',
      Work_Order_No: slump.Work_Order_No || '',
      Contract_No: slump.Contract_No || '',
      Job_title: slump.Job_title || '',
      Address: slump.Address || '',
      Concreting_Location: slump.Concreting_Location || '',
      Slump_Project: slump.Slump_Project || '',
      Location_Modified: slump.Location_Modified || ''
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      Mix_ID: '',
      Acceptable_range: '',
      Date: '',
      Grade: '',
      Plant: '',
      Supplier: '',
      Design_Slump: '',
      No_Of_Truck: '',
      Docket_No: '',
      Time_Water_Add: '',
      Time_On_Site: '',
      Time_Of_Sampling: '',
      Time_Of_Test_1_Start: '',
      Time_Of_Test_1_Comp: '',
      Time_Of_Test_2_Start: '',
      Time_Of_Test_2_Comp: '',
      Time_Of_Test_Fin: '',
      Measured_Slump_1: '',
      Measured_Slump_2: '',
      Form_Of_Slump_1: '',
      Form_Of_Slump_2: '',
      Slump_plate_No: '',
      Result_Of_Slump_Test: '',
      Slump_Carried_Out_By: '',
      Total_Vol: '',
      Amb_Temp: '',
      Conc_Temp: '',
      Weather: '',
      Photo_No: '',
      Time_Of_Cube_Com: '',
      Certificate_No: '',
      Cube_Mark: '',
      Age_day: '',
      Mould_No: '',
      Cube_Carried_Out_By: '',
      RFID: '',
      Remark: '',
      Term_Contract: '',
      Work_Order_No: '',
      Contract_No: '',
      Job_title: '',
      Address: '',
      Concreting_Location: '',
      Slump_Project: '',
      Location_Modified: ''
    });
    setEditingSlump(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  // Excel 相關函數
  const downloadTemplate = async () => {
    try {
      // 直接從 build 目錄複製模板文件
      const response = await fetch('/Site_Slump_Temp_Empty.xlsx');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // 下載文件
      saveAs(blob, 'Site_Slump_Template.xlsx');
      showSnackbar(t('pages.siteSlump.messages.templateDownloaded'), 'success');
    } catch (error) {
      console.error('下載模板失敗:', error);
      showSnackbar(t('pages.siteSlump.messages.templateDownloadError'), 'error');
    }
  };

  // 導出搜索結果為Excel（使用後端API）
  const exportSearchResults = async () => {
    try {
      if (totalCount === 0) {
        showSnackbar(t('pages.siteSlump.messages.noDataToExport'), 'error');
        return;
      }

      // 準備查詢參數（不分頁，導出所有符合條件的記錄）
      const exportQueryParams = {
        ...queryParams,
        Page: 1,
        PageSize: 10000 // 設置一個很大的數字來獲取所有記錄
      };

      // 調用後端API導出Excel
      const response = await apiService.exportSiteSlumpsExcel({
        queryParams: exportQueryParams
      });

      // 創建Blob並下載
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // 從響應頭獲取文件名，如果沒有則使用默認名稱
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `Site_Slump_Export_${new Date().toISOString().split('T')[0]}.xlsx`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      // 下載文件
      saveAs(blob, fileName);
      showSnackbar(t('pages.siteSlump.messages.exportSuccess').replace('{count}', totalCount.toString()), 'success');
    } catch (error) {
      console.error('導出Excel失敗:', error);
      showSnackbar(t('pages.siteSlump.messages.exportError'), 'error');
    }
  };

  // 導出17Form（按照原始邏輯）
  const exportForm17 = async () => {
    try {
      if (totalCount === 0) {
        showSnackbar('沒有數據可導出17Form', 'error');
        return;
      }

      if (!queryParams.Contract_No) {
        showSnackbar('使用匯出17Form功能時必須輸入Contract No', 'error');
        return;
      }

      if (queryParams.startTime && queryParams.endTime) {
        const startDate = new Date(queryParams.startTime);
        const endDate = new Date(queryParams.endTime);
        const daysDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

        if (daysDiff > 7) {
          showSnackbar('使用匯出17Form功能時必須設置7天內的測試日期範圍', 'error');
          return;
        }
      }

      // 按照原始邏輯準備查詢參數
      const request: Form17ExportRequest = {
        queryParams: {
          Docket_No: queryParams.Docket_No,
          Contract_No: queryParams.Contract_No,
          Cube_Mark: queryParams.RFID, // 使用RFID作為Cube_Mark
          Time_Of_Water_Add: queryParams.Docket_No, // 使用Docket_No作為Time_Of_Water_Add
          startTime: queryParams.startTime,
          endTime: queryParams.endTime,
          Staff: queryParams.Uploaded_By,
          search: queryParams.Docket_No,
          searchID: queryParams.Docket_No
        }
      };

      const blob = await apiService.exportForm17(request);
      const fileName = `Form17_${new Date(queryParams.endTime || '').toISOString().split('T')[0]}.zip`;

      saveAs(blob, fileName);
      showSnackbar('成功導出17Form PDF文件', 'success');
    } catch (error) {
      console.error('導出17Form失敗:', error);
      showSnackbar('導出17Form失敗', 'error');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showSnackbar(t('pages.siteSlump.messages.invalidFileType'), 'error');
      return;
    }

    setUploadDialog(true);
    processExcelFile(file);
  };

  const processExcelFile = async (file: File) => {
    try {
      setUploading(true);
      setUploadProgress(0);

      // 模擬上傳進度
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // 直接發送文件到後端，不進行前端解析
      const success = await batchCreateSiteSlumps(file);

      clearInterval(progressInterval);
      setUploadProgress(100);
      setTimeout(() => {
        setUploadDialog(false);
        setUploading(false);
        setUploadProgress(0);

        if (success) {
          //showSnackbar(t('pages.siteSlump.messages.importSuccess'), 'success');
          // 刷新數據
          //fetchSlumps();
        } else {
          showSnackbar('Excel 導入失敗', 'error');
          resetFileInput();
        }
      }, 500);
    } catch (error) {
      resetFileInput();
      console.error('上傳文件失敗:', error);
      showSnackbar(t('pages.siteSlump.messages.fileUploadError'), 'error');
      setUploadDialog(false);
      setUploading(false);
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  // 批量創建 Site Slump 記錄 - 直接發送 Excel 文件到後端
  const batchCreateSiteSlumps = async (file: File) => {
    try {
      // 創建 FormData 對象，直接使用上傳的文件
      const formData = new FormData();
      formData.append('file', file);

      // 調用後端 Excel 導入 API
      const response = await apiService.batchCreateSiteSlumps(formData);

      if (response.data && response.data.success) {
        fetchSlumps();
        resetFileInput();
        console.log('導入成功:', response.data);
        showSnackbar(t('pages.siteSlump.messages.importSuccess').replace('{count}', response.data.successCount.toString()), 'success');

        // 如果有錯誤，顯示錯誤對話框
        if (response.data.errors && response.data.errors.length > 0) {
          setErrorDialog({
            open: true,
            errors: response.data.errors,
            successCount: response.data.successCount,
            errorCount: response.data.errorCount,
            message: response.data.message
          });
        }
        return true;
      } else {
        showSnackbar('Excel 導入失敗', 'error');
        resetFileInput();

        // 顯示錯誤對話框
        if (response.data?.errors && response.data.errors.length > 0) {
          setErrorDialog({
            open: true,
            errors: response.data.errors,
            successCount: response.data.successCount || 0,
            errorCount: response.data.errorCount || 0,
            message: response.data.message || '導入失敗'
          });
        }
        return false;
      }
    } catch (error: any) {
      console.error('批量創建失敗:', error);

      // 顯示錯誤對話框
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        setErrorDialog({
          open: true,
          errors: error.response.data.errors,
          successCount: error.response.data.successCount || 0,
          errorCount: error.response.data.errorCount || 0,
          message: error.response.data.message || '導入失敗'
        });
      } else {
        showSnackbar('導入失敗: ' + (error.response?.data?.message || error.message || '未知錯誤'), 'error');
      }
      return false;
    }
  };

  return (
    <PageLayout title={t('pages.siteSlump.title')}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('pages.siteSlump.fields.Docket_No')}
              value={queryParams.Docket_No}
              onChange={(e) => setQueryParams({ ...queryParams, Docket_No: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('pages.siteSlump.fields.RFID')}
              value={queryParams.RFID}
              onChange={(e) => setQueryParams({ ...queryParams, RFID: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('pages.siteSlump.fields.uploadBy')}
              value={queryParams.Uploaded_By}
              onChange={(e) => setQueryParams({ ...queryParams, Uploaded_By: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Autocomplete
              freeSolo
              disabled={isStaff}
              options={contractNumbers}
              value={isStaff ? user?.userId || '' : queryParams.Contract_No}
              onInputChange={(event, newInputValue) => {
                if (!isStaff) {
                  setQueryParams({ ...queryParams, Contract_No: newInputValue });
                }
              }}
              onChange={(event, newValue) => {
                if (!isStaff) {
                  setQueryParams({ ...queryParams, Contract_No: newValue || '' });
                }
              }}
              loading={loadingContracts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label={t('pages.siteSlump.fields.Contract_No')}
                  onKeyDown={handleKeyDown}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingContracts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('pages.siteSlump.fields.testStartTime')}
              value={queryParams.startTime}
              onChange={(e) => setQueryParams({ ...queryParams, startTime: e.target.value })}
              onKeyDown={handleKeyDown}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('pages.siteSlump.fields.testEndTime')}
              value={queryParams.endTime}
              onChange={(e) => setQueryParams({ ...queryParams, endTime: e.target.value })}
              onKeyDown={handleKeyDown}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} md={6} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            onClick={() => {
              // 驗證時間範圍
              if (queryParams.startTime && queryParams.endTime) {
                const startDate = new Date(queryParams.startTime);
                const endDate = new Date(queryParams.endTime);

                if (endDate < startDate) {
                  showSnackbar('結束時間不能早於開始時間，請重新選擇', 'warning');
                  return;
                }
              }

              setCurrentPage(1);
              fetchSlumps(1);
            }}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            {loading ? t('common.loading') : t('common.query')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            {t('common.reset')}
          </Button>
          {canVerify && <Button
            variant="contained"
            color="success"
            startIcon={<VerifyIcon />}
            onClick={handleBulkVerify}
            disabled={totalCount === 0 || loading || !canVerify}
            sx={{ mr: 1 }}
          >
            {t('pages.siteSlump.actions.bulkVerify')}
          </Button>
          }
          {canVerify && <Button
            variant="outlined"
            color="warning"
            startIcon={<VerifyIcon />}
            onClick={handleBulkUnverify}
            disabled={totalCount === 0 || loading || !canVerify}
            sx={{ mr: 1 }}
          >
            {t('pages.siteSlump.actions.bulkUnverify')}
          </Button>
          }

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            sx={{
              mr: 1,
              borderColor: '#4caf50',
              color: '#4caf50',
              '&:hover': {
                borderColor: '#45a049',
                backgroundColor: '#f1f8e9'
              }
            }}
          >
            {t('pages.siteSlump.messages.downloadTemplate')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportSearchResults}
            disabled={totalCount === 0 || loading}
            sx={{
              mr: 1,
              borderColor: '#2196f3',
              color: '#2196f3',
              '&:hover': {
                borderColor: '#1976d2',
                backgroundColor: '#e3f2fd'
              }
            }}
          >
            {t('pages.siteSlump.messages.exportExcel')}
          </Button>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportForm17}
            disabled={loading}
            sx={{
              mr: 1,
              borderColor: '#ff9800',
              color: '#ff9800',
              '&:hover': {
                borderColor: '#f57c00',
                backgroundColor: '#fff3e0'
              }
            }}
          >
            {t('pages.siteSlump.actions.exportForm17PDF')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={openFileDialog}
            sx={{
              mr: 1,
              borderColor: '#ff9800',
              color: '#ff9800',
              '&:hover': {
                borderColor: '#f57c00',
                backgroundColor: '#fff3e0'
              }
            }}
            disabled={isReader}
          >
            {t('pages.siteSlump.messages.uploadExcel')}
          </Button>
          {!isStaff && <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => {
              // 構建 URL 參數，傳遞當前搜索過濾條件
              const params = new URLSearchParams();
              if (queryParams.Docket_No) {
                params.append('Docket_No', queryParams.Docket_No);
              }
              if (queryParams.Contract_No) {
                params.append('Contract_No', queryParams.Contract_No);
              }
              if (queryParams.startTime) {
                params.append('startTime', queryParams.startTime);
              }
              if (queryParams.endTime) {
                params.append('endTime', queryParams.endTime);
              }
              navigate(`/site-slump/slump-birth-paper?${params.toString()}`);
            }}
            sx={{
              borderColor: '#ff9800',
              color: '#ff9800',
              '&:hover': {
                borderColor: '#f57c00',
                backgroundColor: '#fff3e0'
              }
            }}
          >
            {t('pages.siteSlump.slumpBirthPaper')}
          </Button>
          }
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table>
          <SortableTableHead
            columns={[
              { key: 'id', label: t('pages.siteSlump.fields.id') },
              { key: 'rfid', label: t('pages.siteSlump.fields.RFID') },
              { key: 'docketNo', label: t('pages.siteSlump.fields.Docket_No') },
              { key: 'contractNo', label: t('pages.siteSlump.fields.contractNo') },
              { key: 'testDate', label: t('pages.siteSlump.fields.Date') },
              { key: 'uploadDate', label: t('pages.siteSlump.fields.Upload_date') },
              { key: 'uploadBy', label: t('pages.siteSlump.fields.Upload_By') },
              { key: 'isVerified', label: t('pages.siteSlump.fields.status') },
              { key: 'verifiedBy', label: t('pages.siteSlump.fields.VerifyBy') },
              { key: 'verificationDate', label: t('pages.siteSlump.fields.VerifyDate') },
              { key: 'editRecord', label: t('pages.siteSlump.fields.Edit_Record'), sortable: false },
              { key: 'actions', label: t('pages.siteSlump.fields.actions'), sortable: false }
            ]}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('common.loading')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : slumps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('pages.siteSlump.messages.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((slump) => (
                <TableRow key={slump.Report_ID}>
                  <TableCell>{slump.Report_ID}</TableCell>
                  <TableCell>{slump.RFID}</TableCell>
                  <TableCell>{slump.Docket_No}</TableCell>
                  <TableCell>{slump.Contract_No}</TableCell>
                  <TableCell>{slump.Date ? new Date(slump.Date).toLocaleDateString('en-GB') : '-'}</TableCell>
                  <TableCell>{slump.Upload_date ? new Date(slump.Upload_date).toLocaleDateString('en-GB') : '-'}</TableCell>
                  <TableCell>{slump.Upload_By}</TableCell>
                  <TableCell>{
                    slump.Verify !== '' ? (
                      canVerify ? <Button disabled={!canVerify} onClick={() => handleVerify(slump.Report_ID, slump.Verify)} color="primary" size="small">
                        {slump.Verify === 'F' ? t('common.verifiedAction') : t('common.unverifiedAction')}
                      </Button> : slump.Verify === 'F' ? t('common.unverified') : t('common.verified')
                    ) : t('common.unavailable')
                  }
                  </TableCell>
                  <TableCell>{slump.VerifyBy || '-'}</TableCell>
                  <TableCell>{slump.VerifyDate ? new Date(slump.VerifyDate).toLocaleDateString('en-GB') : '-'}</TableCell>
                  <TableCell>{
                    slump.Edit_Record ?
                      <Button onClick={() => window.alert(slump.Edit_Record)} color="primary" size="small">
                        {t('pages.siteSlump.fields.record')}
                      </Button>
                      : '-'
                  }</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {/* 編輯按鈕 */}

                      <IconButton
                        onClick={() => handleEdit(slump)}
                        color="primary"
                        size="small"
                        disabled={!canEditRecord(slump) || !isAdminOrManager}
                        title={canEditRecord(slump) ? t('common.edit') : getEditRestrictionInfo(slump) || t('common.notAvailableToEdit')}
                      >
                        <EditIcon />
                      </IconButton>

                      {/* 刪除按鈕 */}
                      {isAdmin && (
                        <IconButton onClick={() => handleDelete(slump.Report_ID)} color="error" size="small">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分頁組件 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t('common.showing')} {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} {t('common.of')} {totalCount} {t('common.records')}
            </Typography>
          </Stack>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingSlump ? t('pages.siteSlump.editSlump') : t('pages.siteSlump.addSlump')}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* 基本信息 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('pages.siteSlump.fields.basicInfo')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.date')}
                type="date"
                value={formData.Date}
                onChange={(e) => setFormData({ ...formData, Date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={contractNumbers}
                value={formData.Contract_No}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, Contract_No: newInputValue });
                }}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, Contract_No: newValue || '' });
                }}
                loading={loadingContracts}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t('pages.siteSlump.fields.contractNo')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingContracts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.Docket_No')}
                value={formData.Docket_No}
                onChange={(e) => setFormData({ ...formData, Docket_No: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                disabled
                label={t('pages.siteSlump.fields.mixID')}
                value={formData.Mix_ID}
                onChange={(e) => setFormData({ ...formData, Mix_ID: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.grade')}
                value={formData.Grade}
                onChange={(e) => setFormData({ ...formData, Grade: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.plant')}
                value={formData.Plant}
                onChange={(e) => setFormData({ ...formData, Plant: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.supplier')}
                value={formData.Supplier}
                onChange={(e) => setFormData({ ...formData, Supplier: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                disabled
                label={t('pages.siteSlump.fields.acceptableRange')}
                value={formData.Acceptable_range}
                onChange={(e) => setFormData({ ...formData, Acceptable_range: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.designSlump')}
                value={formData.Design_Slump}
                onChange={(e) => setFormData({ ...formData, Design_Slump: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.noOfTruck')}
                value={formData.No_Of_Truck}
                onChange={(e) => setFormData({ ...formData, No_Of_Truck: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.timeWaterAdd')}
                value={formData.Time_Water_Add}
                onChange={(e) => setFormData({ ...formData, Time_Water_Add: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.timeOnSite')}
                value={formData.Time_On_Site}
                onChange={(e) => setFormData({ ...formData, Time_On_Site: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.timeOfSampling')}
                value={formData.Time_Of_Sampling}
                onChange={(e) => setFormData({ ...formData, Time_Of_Sampling: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.timeOfTest1Start')}
                value={formData.Time_Of_Test_1_Start}
                onChange={(e) => setFormData({ ...formData, Time_Of_Test_1_Start: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.timeOfTest1Comp')}
                value={formData.Time_Of_Test_1_Comp}
                onChange={(e) => setFormData({ ...formData, Time_Of_Test_1_Comp: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.timeOfTest2Start')}
                value={formData.Time_Of_Test_2_Start}
                onChange={(e) => setFormData({ ...formData, Time_Of_Test_2_Start: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.timeOfTest2Comp')}
                value={formData.Time_Of_Test_2_Comp}
                onChange={(e) => setFormData({ ...formData, Time_Of_Test_2_Comp: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.timeOfTestFin')}
                value={formData.Time_Of_Test_Fin}
                onChange={(e) => setFormData({ ...formData, Time_Of_Test_Fin: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t('pages.siteSlump.fields.measuredSlump1')}
                value={formData.Measured_Slump_1}
                onChange={(e) => setFormData({ ...formData, Measured_Slump_1: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t('pages.siteSlump.fields.measuredSlump2')}
                value={formData.Measured_Slump_2}
                onChange={(e) => setFormData({ ...formData, Measured_Slump_2: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.formOfSlump1')}
                value={formData.Form_Of_Slump_1}
                onChange={(e) => setFormData({ ...formData, Form_Of_Slump_1: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.formOfSlump2')}
                value={formData.Form_Of_Slump_2}
                onChange={(e) => setFormData({ ...formData, Form_Of_Slump_2: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.slumpPlateNo')}
                value={formData.Slump_plate_No}
                onChange={(e) => setFormData({ ...formData, Slump_plate_No: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.resultOfSlumpTest')}
                value={formData.Result_Of_Slump_Test}
                onChange={(e) => setFormData({ ...formData, Result_Of_Slump_Test: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.testedBy')}
                value={formData.Slump_Carried_Out_By}
                onChange={(e) => setFormData({ ...formData, Slump_Carried_Out_By: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.totalVolume')}
                value={formData.Total_Vol}
                onChange={(e) => setFormData({ ...formData, Total_Vol: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.ambTemp')}
                value={formData.Amb_Temp}
                onChange={(e) => setFormData({ ...formData, Amb_Temp: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.concreteTemperature')}
                value={formData.Conc_Temp}
                onChange={(e) => setFormData({ ...formData, Conc_Temp: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.weather')}
                value={formData.Weather}
                onChange={(e) => setFormData({ ...formData, Weather: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.photoNo')}
                value={formData.Photo_No}
                onChange={(e) => setFormData({ ...formData, Photo_No: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.timeOfCubeCom')}
                value={formData.Time_Of_Cube_Com}
                onChange={(e) => setFormData({ ...formData, Time_Of_Cube_Com: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.certificateNo')}
                value={formData.Certificate_No}
                onChange={(e) => setFormData({ ...formData, Certificate_No: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.cubeMark')}
                value={formData.Cube_Mark}
                onChange={(e) => setFormData({ ...formData, Cube_Mark: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t('pages.siteSlump.fields.ageDay')}
                value={formData.Age_day}
                onChange={(e) => setFormData({ ...formData, Age_day: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.mouldNo')}
                value={formData.Mould_No}
                onChange={(e) => setFormData({ ...formData, Mould_No: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.cubeCarriedOutBy')}
                value={formData.Cube_Carried_Out_By}
                onChange={(e) => setFormData({ ...formData, Cube_Carried_Out_By: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.rfid')}
                value={formData.RFID}
                onChange={(e) => setFormData({ ...formData, RFID: e.target.value })}
                inputProps={{ maxLength: 11 }}
                helperText={`${formData.RFID.length}/11`}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.remark')}
                value={formData.Remark}
                onChange={(e) => setFormData({ ...formData, Remark: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.termContract')}
                value={formData.Term_Contract}
                onChange={(e) => setFormData({ ...formData, Term_Contract: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.workOrderNo')}
                value={formData.Work_Order_No}
                onChange={(e) => setFormData({ ...formData, Work_Order_No: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.jobTitle')}
                value={formData.Job_title}
                onChange={(e) => setFormData({ ...formData, Job_title: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.address')}
                value={formData.Address}
                onChange={(e) => setFormData({ ...formData, Address: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.concretingLocation')}
                value={formData.Concreting_Location}
                onChange={(e) => setFormData({ ...formData, Concreting_Location: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.slumpProject')}
                value={formData.Slump_Project}
                onChange={(e) => setFormData({ ...formData, Slump_Project: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pages.siteSlump.fields.locationModified')}
                value={formData.Location_Modified}
                onChange={(e) => setFormData({ ...formData, Location_Modified: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingSlump ? t('common.save') : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 驗證確認對話框 */}
      <Dialog open={verifyDialog.open} onClose={() => setVerifyDialog({ open: false, id: 0, verify: '' })}>
        <DialogTitle>
          {t('pages.siteSlump.messages.confirmVerify')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {verifyDialog.verify === 'F'
              ? t('pages.siteSlump.messages.confirmVerifyMessage')
              : t('pages.siteSlump.messages.confirmUnverifyMessage')
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog({ open: false, id: 0, verify: '' })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmVerify} variant="contained" color="primary">
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 批量核實確認對話框 */}
      <Dialog open={bulkVerifyDialog.open} onClose={() => setBulkVerifyDialog({ open: false, action: '' })}>
        <DialogTitle>
          {bulkVerifyDialog.action === 'verify'
            ? t('pages.siteSlump.messages.confirmBulkVerify')
            : t('pages.siteSlump.messages.confirmBulkUnverify')
          }
        </DialogTitle>
        <DialogContent>
          <Typography>
            {bulkVerifyDialog.action === 'verify'
              ? t('pages.siteSlump.messages.confirmBulkVerifyMessage').replace('{count}', totalCount.toString())
              : t('pages.siteSlump.messages.confirmBulkUnverifyMessage').replace('{count}', totalCount.toString())
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('pages.siteSlump.messages.bulkVerifyNote')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkVerifyDialog({ open: false, action: '' })}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={confirmBulkVerify}
            variant="contained"
            color={bulkVerifyDialog.action === 'verify' ? 'success' : 'warning'}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? t('pages.siteSlump.messages.processingBulkOperation').replace('{count}', totalCount.toString()) : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 隱藏的文件輸入 */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
      />

      {/* 上傳進度對話框 */}
      <Dialog open={uploadDialog} onClose={() => !uploading && setUploadDialog(false)}>
        <DialogTitle>{t('pages.siteSlump.messages.uploadExcel')}</DialogTitle>
        <DialogContent>
          <Box sx={{ width: 400, pt: 2 }}>
            {uploading ? (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t('pages.siteSlump.messages.processingFile')}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {uploadProgress}%
                </Typography>
              </>
            ) : (
              <Typography variant="body2">
                {t('pages.siteSlump.messages.fileProcessed')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadDialog(false)}
            disabled={uploading}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel 導入錯誤對話框 */}
      <ExcelImportErrorDialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ ...errorDialog, open: false })}
        errors={errorDialog.errors}
        successCount={errorDialog.successCount}
        errorCount={errorDialog.errorCount}
        message={errorDialog.message}
      />

      {/* 編輯驗證錯誤對話框 */}
      <EditValidationErrorDialog
        open={editValidationErrorDialog.open}
        onClose={() => setEditValidationErrorDialog({ ...editValidationErrorDialog, open: false })}
        errors={editValidationErrorDialog.errors}
        recordId={editValidationErrorDialog.recordId}
        recordInfo={editValidationErrorDialog.recordInfo}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default SiteSlumpPage;